import React from 'react';

/**
 * Page d'accueil publique (marketing), fusionnée depuis l'ancien site statique séparé
 * (homepage/index.html, déployé sur l-iamani-accueil.pages.dev) pour vivre maintenant sur "/"
 * de la même application — condition nécessaire pour que l-iamani.com serve à la fois l'accueil
 * et la connexion sous un seul domaine. Portée quasiment telle quelle (même CSS, même widget de
 * réservation en JS vanilla) dans une Shadow DOM plutôt que réécrite en React idiomatique : ce
 * contenu a ses propres sélecteurs CSS génériques (.card, .grid, .btn, header, footer, section...)
 * qui entreraient en collision avec les classes Tailwind/utilitaires du reste de l'app si elles
 * partageaient le même document — la Shadow DOM les isole complètement, dans les deux sens, sans
 * avoir à préfixer des centaines de sélecteurs à la main.
 */

const STYLE = `
  * { box-sizing: border-box; }
  :host {
    display: block; scroll-behavior: smooth; min-height: 100vh;
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    background: var(--bg);
    color: var(--text);
    line-height: 1.5;
    overflow-x: hidden;
    --brand: #987b55;
    --brand-light: #c9ab7d;
    --brand-dark: #7a6244;
    --bg: #030712;
    --panel: #17130e;
    --panel-solid: #15120e;
    --text: #f4efe6;
    --text-muted: #a99d8b;
    --border: rgba(201, 171, 125, 0.16);
    --radius: 16px;
  }
  a { color: inherit; }
  img { max-width: 100%; }
  .wrap { max-width: 1160px; margin: 0 auto; padding: 0 24px; }

  .sky { position: fixed; inset: 0; overflow: hidden; pointer-events: none; z-index: 0; }
  .sky::before {
    content: ''; position: absolute; inset: 0;
    background:
      radial-gradient(circle at top, rgba(56,189,248,0.16), transparent 0%, transparent 42%),
      radial-gradient(circle at bottom, rgba(99,102,241,0.16), transparent 0%, transparent 48%);
  }
  .star {
    position: absolute; border-radius: 999px; background: rgba(255,255,255,0.9);
    animation-name: starFall; animation-timing-function: linear; animation-iteration-count: infinite;
  }
  .meteor {
    position: absolute; top: -10%; height: 1px; width: 140px;
    background: linear-gradient(90deg, #fff, #a5f3fc, transparent);
    animation-name: meteorMove; animation-timing-function: ease-in; animation-iteration-count: infinite;
  }
  @keyframes starFall {
    0% { transform: translate(0, -30px); opacity: 0; }
    8% { opacity: .95; }
    70% { opacity: .55; }
    100% { transform: translate(var(--drift), 900px); opacity: 0; }
  }
  @keyframes meteorMove {
    0% { transform: translate(0, 0); opacity: 0; }
    12% { opacity: 1; }
    100% { transform: translate(-180px, 520px); opacity: 0; }
  }

  .page { position: relative; z-index: 1; }

  header {
    position: sticky; top: 0; z-index: 50;
    background: rgba(3, 7, 18, 0.7);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
  }
  header .wrap { display: flex; align-items: center; justify-content: space-between; height: 68px; }
  .logo { height: 22px; display: block; }

  .btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    padding: 10px 20px;
    border-radius: 999px;
    font-weight: 700;
    font-size: 13.5px;
    text-decoration: none;
    border: 1px solid transparent;
    transition: transform .15s ease, box-shadow .15s ease, background .15s ease, border-color .15s ease;
    cursor: pointer;
  }
  .btn-primary {
    background: linear-gradient(135deg, var(--brand-light), var(--brand));
    color: #1a140c;
    box-shadow: 0 6px 18px rgba(152,123,85,0.28);
  }
  .btn-primary:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(152,123,85,0.38); }
  .btn-primary:disabled { opacity: .55; cursor: default; transform: none; box-shadow: none; }
  .btn-ghost { background: transparent; color: var(--text); border-color: var(--border); }
  .btn-ghost:hover { border-color: var(--brand-light); color: var(--brand-light); }

  .hero { padding: 72px 0 64px; }
  .hero-grid { display: grid; grid-template-columns: 1.05fr 0.95fr; gap: 46px; align-items: start; }
  .hero-grid.no-widget { grid-template-columns: 1fr; max-width: 720px; margin: 0 auto; text-align: center; }
  .hero-grid.no-widget .hero-cta { justify-content: center; }
  .hero-grid.no-widget .segments { margin-left: auto; margin-right: auto; }

  .eyebrow {
    display: inline-block;
    font-size: 11px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase;
    color: var(--brand-light);
    background: rgba(152,123,85,0.14);
    border: 1px solid var(--border);
    padding: 6px 14px; border-radius: 999px;
    margin-bottom: 20px;
  }
  h1 {
    font-size: clamp(1.8rem, 3.6vw, 2.7rem);
    font-weight: 800; letter-spacing: -0.02em; line-height: 1.15;
    margin: 0 0 16px;
  }
  h1 span { color: var(--brand-light); }
  .lede { font-size: 15.5px; color: var(--text-muted); max-width: 480px; margin: 0 0 28px; }
  .hero-cta { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 40px; }
  .btn-lg { padding: 13px 26px; font-size: 14px; }

  .segments { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
  .segment {
    background: var(--panel); backdrop-filter: blur(8px);
    border: 1px solid var(--border); border-radius: var(--radius);
    padding: 16px 18px; text-align: left;
  }
  .segment .tag { font-size: 10.5px; font-weight: 800; color: var(--brand-light); text-transform: uppercase; letter-spacing: .06em; }
  .segment p { margin: 6px 0 0; color: var(--text-muted); font-size: 12.5px; }

  .booking-card {
    background: var(--panel-solid);
    border: 1px solid var(--border); border-radius: 22px;
    padding: 26px; backdrop-filter: blur(10px);
    box-shadow: 0 24px 60px rgba(0,0,0,.35);
  }
  .booking-card .bh { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
  .booking-card .bh .ic {
    width: 38px; height: 38px; border-radius: 11px; flex-shrink: 0;
    background: rgba(152,123,85,0.16); color: var(--brand-light);
    display: flex; align-items: center; justify-content: center;
  }
  .booking-card .bh .ic svg { width: 19px; height: 19px; }
  .booking-card .bh h3 { margin: 0; font-size: 15px; font-weight: 800; }
  .booking-card .bh p { margin: 2px 0 0; font-size: 12px; color: var(--text-muted); }

  .field-label { font-size: 10.5px; font-weight: 800; color: var(--text-muted); text-transform: uppercase; letter-spacing: .07em; display: block; margin: 0 0 6px; }
  .field-label-row { display: flex; align-items: center; gap: 6px; }
  .help-btn {
    width: 16px; height: 16px; border-radius: 50%; border: 1px solid var(--border); background: rgba(255,255,255,0.04);
    color: var(--text-muted); font-size: 10px; font-weight: 800; line-height: 1; cursor: pointer; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; margin-bottom: 6px; padding: 0;
  }
  .help-btn:hover { border-color: var(--brand-light); color: var(--brand-light); }
  .help-box { background: rgba(255,255,255,0.04); border: 1px solid var(--border); border-radius: 11px; padding: 10px; margin: 0 0 10px; }
  .help-box img { max-height: 180px; width: auto; max-width: 100%; border-radius: 9px; display: block; margin: 0 auto 8px; }
  .help-box p { font-size: 12px; color: var(--text); margin: 0; white-space: pre-wrap; }
  .field {
    width: 100%; background: rgba(255,255,255,0.04); border: 1px solid var(--border);
    border-radius: 11px; padding: 11px 13px; font-size: 13.5px; color: var(--text);
    font-family: inherit; outline: none; transition: border-color .15s ease;
  }
  .field:focus { border-color: var(--brand-light); }
  .field::placeholder { color: #6f6455; }
  .field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .field-block { margin-bottom: 14px; position: relative; }
  .address-suggestions {
    position: absolute; z-index: 30; top: 100%; left: 0; right: 0; margin-top: 4px;
    background: var(--panel, #1a140c); border: 1px solid var(--border); border-radius: 11px;
    overflow: hidden; max-height: 190px; overflow-y: auto;
  }
  .address-suggestion-btn {
    display: block; width: 100%; text-align: left; padding: 9px 12px; font-size: 12px; color: var(--text);
    background: transparent; border: none; border-bottom: 1px solid var(--border); cursor: pointer;
  }
  .address-suggestion-btn:last-child { border-bottom: none; }
  .address-suggestion-btn:hover { background: rgba(255,255,255,0.06); }

  .slots { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 10px; }
  .slot-btn {
    padding: 9px 6px; border-radius: 9px; border: 1px solid var(--border);
    background: rgba(255,255,255,0.03); color: var(--text); font-size: 12.5px; font-weight: 700;
    cursor: pointer; transition: all .12s ease;
  }
  .slot-btn:hover { border-color: var(--brand-light); }
  .slot-btn.active { background: var(--brand); border-color: var(--brand); color: #1a140c; }
  .type-toggle { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .type-btn {
    padding: 10px; border-radius: 10px; border: 1px solid var(--border);
    background: rgba(255,255,255,0.03); color: var(--text-muted); font-size: 12.5px; font-weight: 700;
    cursor: pointer; transition: all .12s ease;
  }
  .type-btn.active { background: rgba(152,123,85,0.18); border-color: var(--brand-light); color: var(--brand-light); }
  .booking-msg { font-size: 12px; color: var(--text-muted); margin: 8px 0 0; }
  .booking-error { font-size: 12px; color: #e08a7a; margin: 10px 0 0; }
  .booking-success { text-align: center; padding: 20px 4px; }
  .booking-success .check { width: 44px; height: 44px; margin: 0 auto 14px; border-radius: 999px; background: rgba(152,123,85,0.18); display: flex; align-items: center; justify-content: center; color: var(--brand-light); }
  .booking-success h4 { margin: 0 0 8px; font-size: 15px; }
  .booking-success p { margin: 0; font-size: 12.5px; color: var(--text-muted); }
  .hidden { display: none !important; }
  .tpl-btn {
    width: 100%; text-align: left; padding: 12px 14px; border-radius: 12px;
    border: 1px solid var(--border); background: rgba(255,255,255,0.03); color: var(--text);
    cursor: pointer; transition: all .15s ease; margin-bottom: 8px; font-family: inherit;
  }
  .tpl-btn:hover { border-color: var(--brand-light); background: rgba(152,123,85,0.08); }
  .tpl-btn .name { font-size: 13.5px; font-weight: 700; }
  .tpl-btn .dur { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
  .back-link { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 700; color: var(--text-muted); background: none; border: none; cursor: pointer; padding: 0; margin-bottom: 14px; font-family: inherit; }
  .back-link:hover { color: var(--brand-light); }
  .choice-btn { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 8px; text-align: left; padding: 10px 13px; border-radius: 11px; border: 1px solid var(--border); background: rgba(255,255,255,0.03); color: var(--text); font-size: 13px; font-weight: 700; cursor: pointer; transition: all .15s ease; margin-bottom: 8px; font-family: inherit; }
  .choice-btn.active { background: rgba(152,123,85,0.16); border-color: var(--brand-light); color: var(--brand-light); }
  .qtype-row { display: flex; gap: 8px; margin-bottom: 8px; }
  .qtype-btn { flex: 1; padding: 9px; border-radius: 9px; border: 1px solid var(--border); background: rgba(255,255,255,0.03); color: var(--text-muted); font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit; }
  .qtype-btn.active { background: rgba(152,123,85,0.16); border-color: var(--brand-light); color: var(--brand-light); }
  .instructions-box { font-size: 12.5px; color: var(--text-muted); background: rgba(255,255,255,0.03); border: 1px solid var(--border); border-radius: 12px; padding: 12px 14px; margin-bottom: 16px; white-space: pre-wrap; }

  section { padding: 60px 0; }
  .section-head { text-align: center; max-width: 560px; margin: 0 auto 40px; }
  .section-head h2 { font-size: clamp(1.4rem, 2.6vw, 1.9rem); font-weight: 800; letter-spacing: -0.01em; margin: 0 0 10px; }
  .section-head p { color: var(--text-muted); margin: 0; font-size: 14.5px; }

  .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
  .card {
    background: var(--panel); backdrop-filter: blur(8px);
    border: 1px solid var(--border); border-radius: var(--radius);
    padding: 20px; transition: transform .18s ease, border-color .18s ease;
  }
  .card:hover { transform: translateY(-2px); border-color: var(--brand-light); }
  .card .icon {
    width: 32px; height: 32px; border-radius: 9px;
    background: rgba(152,123,85,0.14);
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 13px; color: var(--brand-light);
  }
  .card .icon svg { width: 17px; height: 17px; }
  .card h3 { font-size: 14px; font-weight: 700; margin: 0 0 6px; }
  .card p { font-size: 12.5px; line-height: 1.55; color: var(--text-muted); margin: 0; }

  .team {
    background: linear-gradient(150deg, #221a10, #171310);
    border: 1px solid var(--border); border-radius: 26px;
    padding: 48px;
    display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 40px; align-items: center;
  }
  .team .eyebrow { margin-bottom: 16px; }
  .team h2 { font-size: clamp(1.4rem, 2.8vw, 1.9rem); font-weight: 800; margin: 0 0 14px; letter-spacing: -0.01em; }
  .team p.lead { color: var(--text-muted); font-size: 14.5px; margin: 0 0 0; max-width: 480px; }
  .team-points { display: flex; flex-direction: column; gap: 16px; }
  .team-point { display: flex; gap: 14px; align-items: flex-start; }
  .team-point .ic {
    width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0;
    background: rgba(152,123,85,0.16); color: var(--brand-light);
    display: flex; align-items: center; justify-content: center;
  }
  .team-point .ic svg { width: 17px; height: 17px; }
  .team-point h4 { margin: 0 0 3px; font-size: 13.5px; font-weight: 700; }
  .team-point p { margin: 0; font-size: 12.5px; color: var(--text-muted); }

  .band { background: var(--panel-solid); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); }
  .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; text-align: center; }
  .stats .num { font-size: 1.6rem; font-weight: 800; color: var(--brand-light); }
  .stats .label { font-size: 12px; color: var(--text-muted); margin-top: 4px; }

  .cta-final {
    background: linear-gradient(135deg, #241c12, #171310);
    border: 1px solid var(--border);
    border-radius: 24px;
    padding: 52px 28px;
    text-align: center;
  }
  .cta-final h2 { font-size: clamp(1.4rem, 3vw, 2rem); font-weight: 800; margin: 0 0 12px; }
  .cta-final p { color: var(--text-muted); max-width: 440px; margin: 0 auto 26px; font-size: 14px; }

  footer { padding: 40px 0 56px; border-top: 1px solid var(--border); }
  footer .wrap { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px; }
  footer .logo { height: 15px; opacity: .7; }
  footer nav { display: flex; gap: 20px; font-size: 12.5px; color: var(--text-muted); }
  footer nav a { text-decoration: none; }
  footer nav a:hover { color: var(--brand-light); }

  @media (max-width: 900px) {
    .hero-grid { grid-template-columns: 1fr; }
    .team { grid-template-columns: 1fr; padding: 32px 26px; }
    .grid { grid-template-columns: 1fr 1fr; }
    .stats { grid-template-columns: 1fr 1fr; }
  }
  @media (max-width: 640px) {
    .grid { grid-template-columns: 1fr; }
    .segments, .field-row, .slots { grid-template-columns: 1fr 1fr; }
    .stats { grid-template-columns: 1fr 1fr; }
    .hero { padding: 48px 0 40px; }
    .team { padding: 26px 20px; }
  }
`;

const BODY_HTML = `
<div class="sky" id="sky"></div>

<div class="page">
  <header>
    <div class="wrap">
      <img class="logo" id="headerLogo" src="/logo-liamani.png" alt="L'IAmani" />
      <a class="btn btn-primary" href="/login">Se connecter</a>
    </div>
  </header>

  <main>
    <section class="hero">
      <div class="wrap">
        <div class="hero-grid" id="heroGrid">
          <div>
            <span class="eyebrow">Logiciel de gestion tout-en-un</span>
            <h1>Pilotez votre établissement,<br /><span>sans jongler entre dix outils.</span></h1>
            <p class="lede">Planning, CRM, facturation, rendez-vous, démarches client et gestion du personnel — L'IAmani réunit tout ce dont votre équipe a besoin au quotidien, dans une seule plateforme.</p>
            <div class="hero-cta">
              <a class="btn btn-primary btn-lg" href="/login">Se connecter</a>
              <a class="btn btn-ghost btn-lg" href="#modules">Découvrir la plateforme</a>
            </div>
            <div class="segments">
              <div class="segment">
                <span class="tag">Restauration &amp; Hôtellerie</span>
                <p>Planning d'équipe, événementiel, service d'exception, prise de rendez-vous en ligne.</p>
              </div>
              <div class="segment">
                <span class="tag">Prestataires de service</span>
                <p>Catalogue de prestations, réservation en ligne, facturation, suivi client.</p>
              </div>
            </div>
          </div>

          <div id="bookingSlot"></div>
        </div>
      </div>
    </section>

    <section id="modules">
      <div class="wrap">
        <div class="section-head">
          <h2>Une plateforme, tous vos outils</h2>
          <p>Chaque établissement active uniquement les modules dont il a besoin.</p>
        </div>
        <div class="grid">
          <div class="card">
            <div class="icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="16" rx="3"/><path d="M8 3v4M16 3v4M3 10h18"/><circle cx="12" cy="15" r="1.4" fill="currentColor" stroke="none"/></svg></div>
            <h3>Planning &amp; RDV</h3>
            <p>Organisez le planning de vos équipes et laissez vos clients prendre rendez-vous en ligne, 24h/24.</p>
          </div>
          <div class="card">
            <div class="icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M17 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 5 18.5V20"/><circle cx="9.5" cy="8" r="3.25"/><path d="M19 20v-1.5a3.25 3.25 0 0 0-2.2-3.08"/><path d="M15.2 4.9a3.25 3.25 0 0 1 0 6.2"/></svg></div>
            <h3>CRM &amp; contacts</h3>
            <p>Centralisez vos clients et prospects, avec tout l'historique des échanges et des demandes.</p>
          </div>
          <div class="card">
            <div class="icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M5 3h14v17l-2-1.2-2 1.2-2-1.2-2 1.2-2-1.2-2 1.2V3Z"/><path d="M8 8h8M8 12h8M8 16h4"/></svg></div>
            <h3>Facturation</h3>
            <p>Générez des factures conformes en quelques clics et suivez les paiements sans tableur.</p>
          </div>
          <div class="card">
            <div class="icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="m4 7 8 6 8-6"/></svg></div>
            <h3>Démarches client</h3>
            <p>Envoyez des demandes personnalisées à vos contacts (choix, paiement, rendez-vous) et suivez les réponses.</p>
          </div>
          <div class="card">
            <div class="icon"><svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2c.5 4.2 2.3 6 6.5 6.5-4.2.5-6 2.3-6.5 6.5-.5-4.2-2.3-6-6.5-6.5C9.7 8 11.5 6.2 12 2Z"/></svg></div>
            <h3>Événementiel</h3>
            <p>Gérez privatisations et événements de bout en bout, de la demande à la facturation.</p>
          </div>
          <div class="card">
            <div class="icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="2.5" y="5" width="19" height="14" rx="2.5"/><circle cx="8.5" cy="11" r="2"/><path d="M5.5 16c.5-1.8 1.7-2.6 3-2.6s2.5.8 3 2.6"/><path d="M14.5 9.5h5M14.5 13h5M14.5 16h3"/></svg></div>
            <h3>Équipe &amp; RH</h3>
            <p>Postes, employés, congés et absences — toute la gestion RH au même endroit.</p>
          </div>
        </div>
      </div>
    </section>

    <section id="equipe">
      <div class="wrap">
        <div class="team">
          <div>
            <span class="eyebrow">Support sur-mesure</span>
            <h2>Une équipe dédiée, pas un simple service client.</h2>
            <p class="lead">Un outil vous manque ? Une idée pour simplifier votre quotidien ? Notre équipe conçoit et développe des fonctionnalités sur mesure pour votre établissement, directement accessible depuis le bouton support de votre espace. L'IAmani est la seule plateforme qui adapte réellement ses outils à chacun de ses clients.</p>
          </div>
          <div class="team-points">
            <div class="team-point">
              <div class="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12a8 8 0 0 1 16 0v5.5a2.5 2.5 0 0 1-2.5 2.5H16"/><path d="M4 13v3a2 2 0 0 0 2 2h1v-6H5a1 1 0 0 0-1 1Z"/><path d="M20 13v3a2 2 0 0 1-2 2h-1v-6h1a1 1 0 0 1 2 1Z"/></svg></div>
              <div><h4>À l'écoute directe</h4><p>Chaque demande passe par une vraie personne, via le bouton support de votre espace.</p></div>
            </div>
            <div class="team-point">
              <div class="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="m8 9-4 3 4 3M16 9l4 3-4 3M13.5 6.5l-3 11"/></svg></div>
              <div><h4>Développement sur mesure</h4><p>Un besoin spécifique ? Nous concevons l'outil qui manque à votre activité.</p></div>
            </div>
            <div class="team-point">
              <div class="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.5 2"/></svg></div>
              <div><h4>Toujours accessible</h4><p>Votre plateforme évolue avec vos besoins, bien après la mise en place initiale.</p></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="band">
      <div class="wrap">
        <div class="stats">
          <div><div class="num">100%</div><div class="label">Sur-mesure par établissement</div></div>
          <div><div class="num">1</div><div class="label">Plateforme, tous vos outils</div></div>
          <div><div class="num">24/7</div><div class="label">Réservation en ligne</div></div>
          <div><div class="num">FR</div><div class="label">Conçu et hébergé en France</div></div>
        </div>
      </div>
    </section>

    <section>
      <div class="wrap">
        <div class="cta-final">
          <h2>Prêt à simplifier votre gestion ?</h2>
          <p>Connectez-vous à votre espace ou demandez une présentation personnalisée de la plateforme.</p>
          <a class="btn btn-primary btn-lg" href="/login">Se connecter</a>
        </div>
      </div>
    </section>
  </main>

  <footer>
    <div class="wrap">
      <img class="logo" id="footerLogo" src="/logo-liamani.png" alt="L'IAmani" />
      <nav>
        <a href="/legal">Mentions légales</a>
        <a href="/privacy">Confidentialité</a>
        <a href="/login">Se connecter</a>
      </nav>
    </div>
  </footer>
</div>
`;

/** Script d'origine (étoiles/météores, logo dynamique, widget de réservation intégré) — adapté pour
 *  requêter document.getElementById(id) sur la racine (root) fournie plutôt que sur `document` global,
 *  puisque tout ce contenu vit maintenant dans une Shadow DOM. `root.getElementById` fonctionne nativement
 *  (ShadowRoot implémente DocumentOrShadowRoot). Les appels API et les liens de connexion utilisent
 *  désormais des chemins relatifs (/api, /login) puisque ce contenu est servi depuis la même origine
 *  que l'application, alors qu'à l'origine ce site statique séparé pointait vers gestion.l-iamani.com. */
function initHomepageScript(root: ShadowRoot) {
    const sky = root.getElementById('sky');
    if (sky) {
        // Mêmes formules que le champ d'étoiles/météores de la page de connexion (LoginView, App.tsx)
        // pour un fond strictement identique entre les deux pages publiques.
        const starCount = 160;
        for (let i = 0; i < starCount; i++) {
            const s = document.createElement('span');
            s.className = 'star';
            const size = 1.5 + (i % 4);
            const duration = 7 + (i % 7);
            const delay = (i % 18) * 0.35;
            const drift = ((i % 7) - 3) * 10;
            const glow = 7 + (i % 5) * 3;
            s.style.left = ((i * 7.17) % 100) + '%';
            s.style.top = ((i * 11.43) % 100) + '%';
            s.style.width = size + 'px';
            s.style.height = size + 'px';
            s.style.boxShadow = '0 0 ' + glow + 'px rgba(255,255,255,0.8)';
            s.style.setProperty('--drift', drift + 'px');
            s.style.animationDuration = duration + 's';
            s.style.animationDelay = delay + 's';
            sky.appendChild(s);
        }
        const meteorCount = 16;
        for (let j = 0; j < meteorCount; j++) {
            const m = document.createElement('div');
            m.className = 'meteor';
            m.style.left = (4 + j * 6.2) + '%';
            m.style.animationDuration = (3.4 + (j % 3) * 0.7) + 's';
            m.style.animationDelay = (j * 0.9) + 's';
            sky.appendChild(m);
        }
    }

    // Logo de la plateforme : même réglage global que la page de connexion (loginLogo).
    fetch('/api/auth/global-settings')
        .then((r) => r.json())
        .then((data) => {
            if (!data || !data.loginLogo) return;
            ['headerLogo', 'footerLogo'].forEach((id) => {
                const el = root.getElementById(id) as HTMLImageElement | null;
                if (el) el.src = data.loginLogo;
            });
        })
        .catch(() => {});

    // Widget de prise de RDV intégré (grand format, dans le hero) — n'apparaît que si le super admin
    // l'a activé (Compte > Widget de réservation en ligne).
    const API = '/api';
    const heroGrid = root.getElementById('heroGrid');
    const slotEl = root.getElementById('bookingSlot');
    if (!heroGrid || !slotEl) return;

    let clientId: string | null = null;
    let info: any = null;
    let templates: any[] = [];
    let tpl: any = null;
    let selections: Record<string, number> = {};
    let answers: Record<string, string> = {};
    let optionDetails: Record<string, string> = {};
    let openHelp: Record<string, boolean> = {};
    let selectedDate: string | null = null;
    let selectedSlot: string | null = null;
    let contactType = 'PRIVÉ';
    let locationType = 'domicile';
    let address = '';
    let addressSelected = false;
    let addressPostalCode = '';
    let addressCity = '';
    let addressDebounce: ReturnType<typeof setTimeout> | null = null;
    let kmFeeDebounce: ReturnType<typeof setTimeout> | null = null;
    let kmFee: { fee: number; distance_km: number } | null = null;

    // Estimation des frais kilométriques affichée en direct pendant la saisie de l'adresse — pour que le
    // client voie le vrai total (y compris les frais) avant de payer, pas seulement une fois sur Stripe/Revolut.
    function scheduleKmFeeEstimate() {
        if (kmFeeDebounce) clearTimeout(kmFeeDebounce);
        if (!isDomicileRdv() || !address.trim()) { kmFee = null; renderChoices(); return; }
        kmFeeDebounce = setTimeout(() => {
            fetch(API + '/rdv-pro-booking/' + clientId + '/km-fee-estimate?address=' + encodeURIComponent(address.trim())
                + '&postal_code=' + encodeURIComponent(addressPostalCode.trim()) + '&city=' + encodeURIComponent(addressCity.trim()))
                .then((r) => r.json())
                .then((data) => { kmFee = data && data.fee > 0 ? data : null; renderChoices(); })
                .catch(() => { kmFee = null; });
        }, 600);
    }
    const isDomicileRdv = () => !!info && (info.location_mode === 'domicile' || (info.location_mode === 'les_deux' && locationType === 'domicile'));

    const todayInput = () => new Date().toISOString().split('T')[0];
    const esc = (str: string) => { const d = document.createElement('div'); d.textContent = str || ''; return d.innerHTML; };
    const formatPrice = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
    const getSelectedLabels = (answer?: string) => (answer ? answer.split(', ').filter(Boolean) : []);

    const buildAnswer = (q: any) => {
        if (q.type !== 'select') return String(answers[q.id] || '').trim();
        return getSelectedLabels(answers[q.id]).map((label) => {
            const opt = (q.options || []).find((o: any) => o.label === label);
            if (opt && opt.has_detail_field) {
                const detail = String(optionDetails[opt.id] || '').trim();
                return detail ? (label + ' : ' + detail) : label;
            }
            return label;
        }).join(', ');
    };

    const isBlocked = () => {
        if (!tpl) return false;
        return (tpl.custom_questions || []).some((q: any) => {
            if (q.type !== 'select') return false;
            const selected = getSelectedLabels(answers[q.id]);
            return (q.options || []).some((o: any) => o.block && selected.indexOf(o.label) !== -1);
        });
    };

    function renderTypePicker() {
        let html = '<div class="booking-card"><div class="bh">' +
            '<div class="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="16" rx="3"/><path d="M8 3v4M16 3v4M3 10h18"/><circle cx="12" cy="15" r="1.4" fill="currentColor" stroke="none"/></svg></div>' +
            '<div><h3>Prendre rendez-vous</h3><p>Choisissez le type de rendez-vous</p></div></div><div>';
        templates.forEach((t, i) => {
            html += '<button type="button" class="tpl-btn" data-i="' + i + '"><div class="name">' + esc(t.name) + '</div>' +
                (t.rdv_duration_minutes ? '<div class="dur">Durée prévue : ' + t.rdv_duration_minutes + ' minutes</div>' : '') + '</button>';
        });
        html += '</div></div>';
        slotEl.innerHTML = html;
        Array.prototype.forEach.call(slotEl.querySelectorAll('.tpl-btn'), (btn: HTMLElement) => {
            btn.addEventListener('click', () => {
                tpl = templates[Number(btn.getAttribute('data-i'))];
                selections = {}; answers = {};
                renderForm();
            });
        });
    }

    function renderForm() {
        let choicesHtml = '';
        if (tpl && (tpl.response_type === 'choice' || tpl.response_type === 'both')) {
            choicesHtml = '<div class="field-block"><span class="field-label">Votre choix' + (tpl.allow_multiple_choices ? ' (plusieurs possibles)' : '') + '</span><div id="bChoices"></div></div>';
        }
        let questionsHtml = '';
        if (tpl && (tpl.custom_questions || []).length > 0) {
            questionsHtml = '<div id="bQuestions"></div>';
        }
        const backHtml = templates.length > 1 ? '<button type="button" class="back-link" id="bBack">&larr; Changer de type de rendez-vous</button>' : '';
        const instructionsHtml = (tpl && tpl.instructions_text) ? '<div class="instructions-box">' + esc(tpl.instructions_text) + '</div>' : '';

        slotEl.innerHTML =
            '<div class="booking-card">' +
                '<div class="bh">' +
                    '<div class="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="16" rx="3"/><path d="M8 3v4M16 3v4M3 10h18"/><circle cx="12" cy="15" r="1.4" fill="currentColor" stroke="none"/></svg></div>' +
                    '<div><h3>' + esc(tpl ? tpl.name : 'Prendre rendez-vous') + '</h3><p id="bDuration"></p></div>' +
                '</div>' +
                '<div id="bBody">' +
                    backHtml + instructionsHtml + choicesHtml + questionsHtml +
                    (info && info.location_mode === 'les_deux' ?
                        '<div class="field-block">' +
                            '<span class="field-label">Où souhaitez-vous être reçu(e) ?</span>' +
                            '<div class="type-toggle">' +
                                '<button type="button" class="type-btn loc-btn' + (locationType === 'domicile' ? ' active' : '') + '" data-loc="domicile">À mon adresse</button>' +
                                '<button type="button" class="type-btn loc-btn' + (locationType === 'entreprise' ? ' active' : '') + '" data-loc="entreprise">Dans l&#39;établissement</button>' +
                            '</div>' +
                        '</div>' : '') +
                    (info && (info.location_mode === 'domicile' || info.location_mode === 'les_deux') ?
                        '<div class="field-block' + (isDomicileRdv() ? '' : ' hidden') + '" id="bAddressBlock">' +
                            '<span class="field-label">Adresse</span>' +
                            '<input class="field" id="bAddress" placeholder="Numéro et rue" value="' + esc(address) + '" autocomplete="off" />' +
                            '<div id="bAddressSuggestions"></div>' +
                            '<div class="field-row" style="margin-top:10px">' +
                                '<div><span class="field-label">Code postal</span><input class="field" id="bPostalCode" value="' + esc(addressPostalCode) + '" /></div>' +
                                '<div><span class="field-label">Ville</span><input class="field" id="bCity" value="' + esc(addressCity) + '" /></div>' +
                            '</div>' +
                        '</div>' : '') +
                    '<div id="bDateBlock">' +
                        '<div class="field-block">' +
                            '<span class="field-label">Date souhaitée</span>' +
                            '<input type="date" class="field" id="bDate" min="' + todayInput() + '" value="' + (selectedDate || todayInput()) + '" />' +
                        '</div>' +
                        '<div class="field-block" id="bSlotsBlock"><div class="slots" id="bSlots"></div></div>' +
                    '</div>' +
                    '<div id="bContactBlock" class="hidden">' +
                        (info && info.professionals_only ? '' :
                        '<div class="field-block"><div class="type-toggle">' +
                            '<button type="button" class="type-btn active" data-type="PRIVÉ">Particulier</button>' +
                            '<button type="button" class="type-btn" data-type="PROFESSIONNEL">Professionnel</button>' +
                        '</div></div>') +
                        '<div class="field-block' + (contactType === 'PROFESSIONNEL' ? ' hidden' : '') + '" id="bNameBlock"><div class="field-row">' +
                            '<input class="field" id="bFirstName" placeholder="Prénom" />' +
                            '<input class="field" id="bLastName" placeholder="Nom" />' +
                        '</div></div>' +
                        '<div class="field-block' + (contactType === 'PROFESSIONNEL' ? '' : ' hidden') + '" id="bCompanyBlock"><input class="field" id="bCompanyName" placeholder="Nom de la société" /></div>' +
                        '<div class="field-block"><input class="field" id="bPhone" placeholder="Téléphone" /></div>' +
                        '<div class="field-block"><input class="field" id="bEmail" type="email" placeholder="Email" /></div>' +
                        '<div class="field-block"><textarea class="field" id="bNotes" rows="2" placeholder="Message (optionnel)" style="resize:none"></textarea></div>' +
                        '<button type="button" class="btn btn-primary" style="width:100%" id="bSubmit">Envoyer ma demande</button>' +
                        '<p class="booking-error hidden" id="bError"></p>' +
                    '</div>' +
                '</div>' +
            '</div>';

        const durEl = root.getElementById('bDuration');
        const duration = (tpl && tpl.rdv_duration_minutes) || (info && info.duration_minutes);
        if (durEl && duration) durEl.textContent = 'Durée prévue : ' + duration + ' minutes';

        const backBtn = root.getElementById('bBack');
        if (backBtn) backBtn.addEventListener('click', () => { tpl = null; renderTypePicker(); });

        if (tpl && (tpl.response_type === 'choice' || tpl.response_type === 'both')) renderChoices();
        if (tpl && (tpl.custom_questions || []).length > 0) renderQuestions();
        updateDateBlockVisibility();

        root.getElementById('bDate')?.addEventListener('change', (e: any) => { selectedDate = e.target.value; loadSlots(); });
        selectedDate = selectedDate || todayInput();

        Array.prototype.forEach.call(slotEl.querySelectorAll('.type-btn:not(.loc-btn)'), (btn: HTMLElement) => {
            btn.addEventListener('click', () => {
                contactType = btn.getAttribute('data-type') || 'PRIVÉ';
                Array.prototype.forEach.call(slotEl.querySelectorAll('.type-btn:not(.loc-btn)'), (b: HTMLElement) => b.classList.toggle('active', b === btn));
                root.getElementById('bNameBlock')?.classList.toggle('hidden', contactType === 'PROFESSIONNEL');
                root.getElementById('bCompanyBlock')?.classList.toggle('hidden', contactType === 'PRIVÉ');
            });
        });

        Array.prototype.forEach.call(slotEl.querySelectorAll('.loc-btn'), (btn: HTMLElement) => {
            btn.addEventListener('click', () => {
                locationType = btn.getAttribute('data-loc') || 'domicile';
                Array.prototype.forEach.call(slotEl.querySelectorAll('.loc-btn'), (b: HTMLElement) => b.classList.toggle('active', b === btn));
                root.getElementById('bAddressBlock')?.classList.toggle('hidden', !isDomicileRdv());
            });
        });

        const addressInputEl = root.getElementById('bAddress') as HTMLInputElement | null;
        const postalCodeInputEl = root.getElementById('bPostalCode') as HTMLInputElement | null;
        const cityInputEl = root.getElementById('bCity') as HTMLInputElement | null;
        postalCodeInputEl?.addEventListener('input', (e: any) => { addressPostalCode = e.target.value; scheduleKmFeeEstimate(); });
        cityInputEl?.addEventListener('input', (e: any) => { addressCity = e.target.value; scheduleKmFeeEstimate(); });
        addressInputEl?.addEventListener('input', (e: any) => {
            address = e.target.value;
            addressSelected = false;
            scheduleKmFeeEstimate();
            if (addressDebounce) clearTimeout(addressDebounce);
            const suggestionsEl = root.getElementById('bAddressSuggestions');
            if (!suggestionsEl) return;
            if (address.trim().length < 3) { suggestionsEl.innerHTML = ''; return; }
            addressDebounce = setTimeout(() => {
                fetch(API + '/public/address-autocomplete?q=' + encodeURIComponent(address.trim()))
                    .then((r) => r.json())
                    .then((data) => {
                        const suggestions = (data && data.suggestions) || [];
                        suggestionsEl.innerHTML = suggestions.length === 0 ? '' :
                            '<div class="address-suggestions">' +
                                suggestions.map((s: any) => '<button type="button" class="address-suggestion-btn">' + esc(s.label) + '</button>').join('') +
                            '</div>';
                        Array.prototype.forEach.call(suggestionsEl.querySelectorAll('.address-suggestion-btn'), (btn: HTMLElement, i: number) => {
                            btn.addEventListener('mousedown', (ev: Event) => ev.preventDefault());
                            btn.addEventListener('click', () => {
                                const s = suggestions[i];
                                address = s.address || s.label;
                                addressSelected = true;
                                addressPostalCode = s.postcode || '';
                                addressCity = s.city || '';
                                if (addressInputEl) addressInputEl.value = address;
                                if (postalCodeInputEl) postalCodeInputEl.value = addressPostalCode;
                                if (cityInputEl) cityInputEl.value = addressCity;
                                scheduleKmFeeEstimate();
                                suggestionsEl.innerHTML = '';
                            });
                        });
                    })
                    .catch(() => {});
            }, 400);
        });
        addressInputEl?.addEventListener('blur', () => {
            setTimeout(() => { const el = root.getElementById('bAddressSuggestions'); if (el) el.innerHTML = ''; }, 150);
        });

        root.getElementById('bSubmit')?.addEventListener('click', submitBooking);
        loadSlots();
    }

    function updateDateBlockVisibility() {
        const dateBlock = root.getElementById('bDateBlock');
        const contactBlock = root.getElementById('bContactBlock');
        if (!dateBlock) return;
        if (isBlocked()) {
            dateBlock.classList.add('hidden');
            if (contactBlock) contactBlock.classList.add('hidden');
        } else {
            dateBlock.classList.remove('hidden');
        }
    }

    function renderChoices() {
        const el = root.getElementById('bChoices');
        if (!el) return;
        el.innerHTML = '';
        let total = 0;
        (tpl.choices || []).forEach((c: any) => {
            const active = c.id in selections;
            if (active) total += c.price_ttc * (selections[c.id] || 1);
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'choice-btn' + (active ? ' active' : '');
            b.innerHTML = '<span>' + esc(c.label) + '</span>' + (c.price_ttc > 0 ? '<span style="font-weight:400;font-size:11px">' + formatPrice(c.price_ttc) + '</span>' : '');
            b.addEventListener('click', () => {
                if (tpl.allow_multiple_choices) {
                    if (c.id in selections) delete selections[c.id]; else selections[c.id] = 1;
                } else {
                    selections = (c.id in selections) ? {} : { [c.id]: 1 };
                }
                renderChoices();
                // La durée d'un RDV Prestation dépend des prestations sélectionnées — recharge les créneaux
                // si une date est déjà choisie, pour ne pas laisser un créneau devenu trop court/disponible.
                if (selectedDate) loadSlots();
            });
            el.appendChild(b);
        });
        if (kmFee && kmFee.fee > 0) {
            const feeP = document.createElement('p');
            feeP.style.cssText = 'text-align:right;font-size:11px;color:var(--text-muted);margin:6px 0 0';
            feeP.textContent = 'Frais kilométriques (' + kmFee.distance_km + ' km aller-retour) : ' + formatPrice(kmFee.fee);
            el.appendChild(feeP);
        }
        if (total > 0) {
            const p = document.createElement('p');
            p.style.cssText = 'text-align:right;font-size:13px;font-weight:700;margin:6px 0 0';
            p.textContent = 'Total : ' + formatPrice(total + (kmFee ? kmFee.fee : 0));
            el.appendChild(p);
        }
    }

    function renderQuestions() {
        const el = root.getElementById('bQuestions');
        if (!el) return;
        el.innerHTML = '';
        (tpl.custom_questions || []).forEach((q: any) => {
            const block = document.createElement('div');
            block.className = 'field-block';
            const labelRow = document.createElement('div');
            labelRow.className = 'field-label-row';
            const label = document.createElement('span');
            label.className = 'field-label';
            label.textContent = q.question + (q.required ? ' *' : '');
            labelRow.appendChild(label);
            block.appendChild(labelRow);
            if (q.help_text || q.help_image) {
                const helpBtn = document.createElement('button');
                helpBtn.type = 'button';
                helpBtn.className = 'help-btn';
                helpBtn.textContent = '?';
                helpBtn.title = 'Aide';
                helpBtn.addEventListener('click', () => {
                    openHelp[q.id] = !openHelp[q.id];
                    renderQuestions();
                });
                labelRow.appendChild(helpBtn);
                if (openHelp[q.id]) {
                    const helpBox = document.createElement('div');
                    helpBox.className = 'help-box';
                    if (q.help_image) {
                        const helpImg = document.createElement('img');
                        helpImg.src = q.help_image;
                        helpImg.alt = 'Aide';
                        helpBox.appendChild(helpImg);
                    }
                    if (q.help_text) {
                        const helpP = document.createElement('p');
                        helpP.textContent = q.help_text;
                        helpBox.appendChild(helpP);
                    }
                    block.appendChild(helpBox);
                }
            }
            if (q.type === 'select') {
                const row = document.createElement('div');
                row.className = 'qtype-row';
                const selected = getSelectedLabels(answers[q.id]);
                (q.options || []).forEach((o: any) => {
                    const b = document.createElement('button');
                    b.type = 'button';
                    b.className = 'qtype-btn' + (selected.indexOf(o.label) !== -1 ? ' active' : '');
                    b.textContent = o.label;
                    b.addEventListener('click', () => {
                        const current = getSelectedLabels(answers[q.id]);
                        let next: string[];
                        if (q.allow_multiple) {
                            next = current.indexOf(o.label) !== -1 ? current.filter((l) => l !== o.label) : current.concat([o.label]);
                        } else {
                            next = current.indexOf(o.label) !== -1 ? [] : [o.label];
                        }
                        answers[q.id] = next.join(', ');
                        renderQuestions();
                        updateDateBlockVisibility();
                    });
                    row.appendChild(b);
                });
                block.appendChild(row);
                (q.options || []).filter((o: any) => selected.indexOf(o.label) !== -1 && o.message).forEach((o: any) => {
                    const p = document.createElement('p');
                    p.className = 'booking-msg';
                    p.textContent = o.message;
                    block.appendChild(p);
                });
                (q.options || []).filter((o: any) => selected.indexOf(o.label) !== -1 && o.has_detail_field).forEach((o: any) => {
                    const detailInput = document.createElement('input');
                    detailInput.className = 'field';
                    detailInput.placeholder = 'Précisez...';
                    detailInput.value = optionDetails[o.id] || '';
                    detailInput.addEventListener('input', (e: any) => { optionDetails[o.id] = e.target.value; });
                    block.appendChild(detailInput);
                });
            } else {
                const input = document.createElement('input');
                input.className = 'field';
                input.value = answers[q.id] || '';
                input.addEventListener('input', (e: any) => { answers[q.id] = e.target.value; });
                block.appendChild(input);
            }
            el.appendChild(block);
        });
    }

    function loadSlots() {
        if (isBlocked()) return;
        const slotsEl = root.getElementById('bSlots');
        if (!slotsEl) return;
        slotsEl.innerHTML = '<p class="booking-msg">Chargement des créneaux...</p>';
        root.getElementById('bContactBlock')?.classList.add('hidden');
        selectedSlot = null;
        const choicesQuery = tpl && Object.keys(selections).length > 0
            ? '&choices=' + encodeURIComponent(JSON.stringify(Object.entries(selections).map(([id, qty]) => ({ id, qty }))))
            : '';
        fetch(API + '/rdv-pro-booking/' + clientId + '/availability?date=' + encodeURIComponent(selectedDate || '')
            + (tpl ? '&template_id=' + encodeURIComponent(tpl.id) : '') + choicesQuery)
            .then((r) => r.json())
            .then((data) => {
                if (data && data.duration_minutes) {
                    const durEl = root.getElementById('bDuration');
                    if (durEl) durEl.textContent = 'Durée prévue : ' + data.duration_minutes + ' minutes';
                }
                const slots = (data && data.slots) || [];
                if (slots.length === 0) {
                    slotsEl.innerHTML = '<p class="booking-msg">Aucun créneau disponible ce jour-là.</p>';
                    return;
                }
                slotsEl.innerHTML = '';
                slots.forEach((s: string) => {
                    const b = document.createElement('button');
                    b.type = 'button';
                    b.className = 'slot-btn';
                    b.textContent = s;
                    b.addEventListener('click', () => {
                        selectedSlot = s;
                        Array.prototype.forEach.call(slotsEl.querySelectorAll('.slot-btn'), (x: HTMLElement) => x.classList.toggle('active', x === b));
                        root.getElementById('bContactBlock')?.classList.remove('hidden');
                    });
                    slotsEl.appendChild(b);
                });
            })
            .catch(() => { slotsEl.innerHTML = '<p class="booking-msg">Erreur de chargement.</p>'; });
    }

    function showSuccess(paymentLink: string) {
        const bodyEl = slotEl!.querySelector('#bBody');
        if (!bodyEl) return;
        bodyEl.innerHTML =
            '<div class="booking-success">' +
                '<div class="check"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg></div>' +
                '<h4>Demande envoyée</h4>' +
                '<p>Votre demande de rendez-vous a bien été transmise — vous serez recontacté pour confirmation.</p>' +
                (paymentLink ? '<a class="btn btn-primary" style="margin-top:14px" href="' + paymentLink + '" target="_blank" rel="noreferrer">Procéder au paiement</a>' : '') +
            '</div>';
    }

    function submitBooking() {
        const errorEl = root.getElementById('bError');
        errorEl?.classList.add('hidden');
        if (!selectedSlot) return;
        const val = (id: string) => (root.getElementById(id) as HTMLInputElement | null)?.value.trim() || '';
        const phone = val('bPhone');
        const firstName = val('bFirstName');
        const lastName = val('bLastName');
        const companyName = val('bCompanyName');
        const email = val('bEmail');
        const notes = val('bNotes');

        const fail = (msg?: string) => {
            if (errorEl) { errorEl.textContent = msg || "Erreur lors de l'envoi. Merci de réessayer."; errorEl.classList.remove('hidden'); }
            const btn = root.getElementById('bSubmit') as HTMLButtonElement | null;
            if (btn) { btn.disabled = false; btn.textContent = 'Envoyer ma demande'; }
        };

        if (!phone) return fail('Merci de renseigner votre téléphone.');
        if (!email) return fail('Merci de renseigner votre email — indispensable pour recevoir la confirmation de votre rendez-vous.');
        if (contactType === 'PROFESSIONNEL' && !companyName) return fail('Merci de renseigner le nom de votre société.');
        if (contactType === 'PRIVÉ' && !lastName) return fail('Merci de renseigner votre nom.');
        if (tpl && tpl.response_type === 'choice' && Object.keys(selections).length === 0) return fail('Merci de faire un choix.');
        const missingRequired = tpl && (tpl.custom_questions || []).find((q: any) => q.required && !String(answers[q.id] || '').trim());
        if (missingRequired) return fail('Merci de répondre à : ' + missingRequired.question);
        if (isDomicileRdv() && !address.trim()) return fail('Merci d\'indiquer votre adresse pour l\'intervention à domicile.');
        if (isDomicileRdv() && !addressSelected) return fail('Merci de sélectionner votre adresse dans la liste proposée.');

        const btn = root.getElementById('bSubmit') as HTMLButtonElement | null;
        if (btn) { btn.disabled = true; btn.textContent = 'Envoi...'; }

        if (!tpl) {
            fetch(API + '/rdv-pro-booking/' + clientId + '/reserve', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ date: selectedDate, start_time: selectedSlot, type: contactType, first_name: firstName, last_name: lastName, company_name: companyName, phone, email, notes }),
            }).then((r) => r.json())
                .then((res) => { if (res && res.error) { fail(res.error); return; } showSuccess(''); })
                .catch(() => fail());
            return;
        }

        fetch(API + '/rdv-pro-booking/' + clientId + '/start', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ template_id: tpl.id, type: contactType, first_name: firstName, last_name: lastName, company_name: companyName, phone, email }),
        }).then((r) => r.json())
            .then((startRes) => {
                if (startRes && startRes.error) { fail(startRes.error); return null; }
                const token = startRes.token;
                const choicesPayload = Object.keys(selections).map((id) => ({ id, qty: selections[id] }));
                const answersPayload = (tpl.custom_questions || []).map((q: any) => ({ id: q.id, answer: buildAnswer(q) })).filter((a: any) => a.answer.trim());
                const reason = Object.keys(selections).length > 0
                    ? (tpl.choices || []).filter((c: any) => c.id in selections).map((c: any) => c.label + (selections[c.id] > 1 ? ' x' + selections[c.id] : '')).join(', ')
                    : tpl.name;
                const rdvPayload = { date: selectedDate, start_time: selectedSlot, subject: reason, notes, location_type: locationType, address: isDomicileRdv() ? address.trim() : undefined, postal_code: isDomicileRdv() ? addressPostalCode.trim() : undefined, city: isDomicileRdv() ? addressCity.trim() : undefined };
                return fetch(API + '/documentation/' + token + '/respond', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ choices: choicesPayload, text: notes, custom_answers: answersPayload, rdv: rdvPayload }),
                }).then((r) => r.json()).then((respondRes) => {
                    if (respondRes && respondRes.error) { fail(respondRes.error); return null; }
                    // Paiement requis : rien n'est encore enregistré côté serveur (démarche et RDV compris)
                    // tant que Stripe ne confirme pas le paiement — inutile d'appeler book-rdv séparément.
                    if (respondRes.pending_payment) {
                        window.location.href = respondRes.stripe_payment_link;
                        return null;
                    }
                    return fetch(API + '/documentation/' + token + '/book-rdv', {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(rdvPayload),
                    }).then((r) => r.json()).then((bookRes) => {
                        if (bookRes && bookRes.error) { fail(bookRes.error); return; }
                        showSuccess('');
                    });
                });
            })
            .catch(() => fail());
    }

    fetch(API + '/platform-rdv-widget')
        .then((r) => r.json())
        .then((data) => {
            if (!data || !data.enabled || !data.client_id) { heroGrid.classList.add('no-widget'); return null; }
            clientId = data.client_id;
            return Promise.all([
                fetch(API + '/rdv-pro-booking/' + clientId + '/info').then((r) => r.json()),
                fetch(API + '/rdv-pro-booking/' + clientId + '/templates').then((r) => r.json()),
            ]);
        })
        .then((results) => {
            if (!results) return;
            info = results[0];
            if (info && info.professionals_only) contactType = 'PROFESSIONNEL';
            templates = (results[1] && results[1].templates) || [];
            if (templates.length === 1) { tpl = templates[0]; renderForm(); }
            else if (templates.length > 1) { renderTypePicker(); }
            else { renderForm(); }
        })
        .catch(() => { heroGrid.classList.add('no-widget'); });
}

export const PublicHomepage: React.FC = () => {
    const hostRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const host = hostRef.current;
        if (!host) return;
        const previousTitle = document.title;
        document.title = "L'IAmani — La plateforme de gestion pour la Restauration & les Prestataires";

        const shadow = host.attachShadow({ mode: 'open' });
        shadow.innerHTML = `<style>${STYLE}</style>${BODY_HTML}`;
        initHomepageScript(shadow);

        return () => {
            document.title = previousTitle;
        };
    }, []);

    return <div ref={hostRef} />;
};

export default PublicHomepage;
